import React, { useState } from 'react';
import { RackType, AisleType } from '../../store/editorStore';
import {
  Server, Wind, Zap, HelpCircle,
  Network, Box, Thermometer, PlugZap,
  FlameKindling, Snowflake, Lock, ChevronDown, ChevronRight,
  Square,
} from 'lucide-react';

// ─── Rack / Floor Entity palette items ─────────────────────────────────────────
interface RackPaletteItem {
  kind: 'rack';
  type: RackType;
  label: string;
  icon: React.ComponentType<any>;
  desc: string;
  color: string;
  tooltip?: string;
}

// ─── Zone palette items ────────────────────────────────────────────────────────
interface ZonePaletteItem {
  kind: 'zone';
  type: AisleType;
  label: string;
  icon: React.ComponentType<any>;
  desc: string;
  color: string;
  tooltip?: string;
}

type PaletteItem = RackPaletteItem | ZonePaletteItem;

interface PaletteGroup {
  id: string;
  label: string;
  items: PaletteItem[];
}

const GROUPS: PaletteGroup[] = [
  {
    id: 'racks',
    label: 'Стойки',
    items: [
      { kind: 'rack', type: 'server_rack',  label: 'Серверная стойка', icon: Server,  desc: '42U',    color: '#3b82f6', tooltip: 'Стандартная 19" стойка (600×1000мм, 42U). Для монтажа серверов и сетевого оборудования.' },
      { kind: 'rack', type: 'network_rack', label: 'Сетевая стойка',   icon: Network, desc: '19″',    color: '#22d3ee', tooltip: 'Широкая стойка (800×1000мм, 42U). Для коммутаторов, патч-панелей и кросс-панелей.' },
      { kind: 'rack', type: 'open_frame',   label: 'Открытая рама',    icon: Box,     desc: '4-пост', color: '#94a3b8', tooltip: 'Четырёхстоечная открытая рама без стенок и дверей (600×1000мм, 42U). Для серверов с хорошей вентиляцией.' },
    ] satisfies RackPaletteItem[],
  },
  {
    id: 'zones',
    label: 'Коридоры',
    items: [
      { kind: 'zone', type: 'cold',        label: 'Холодный коридор', icon: Snowflake,     desc: 'Фронт стоек', color: '#38bdf8', tooltip: 'Зона между фронтальными сторонами стоек. Холодный воздух поступает от CRAC/In-Row к фронту оборудования.' },
      { kind: 'zone', type: 'hot',         label: 'Горячий коридор',  icon: FlameKindling, desc: 'Тыл стоек',   color: '#f97316', tooltip: 'Зона между тыльными сторонами стоек. Горячий воздух от оборудования отводится вверх к системам охлаждения.' },
      { kind: 'zone', type: 'containment', label: 'Containment',      icon: Lock,          desc: 'Изоляция',    color: '#a855f7', tooltip: 'Замкнутый коридор с торцевыми дверями. Полностью изолирует холодный или горячий поток для максимальной эффективности охлаждения.' },
    ] satisfies ZonePaletteItem[],
  },
  {
    id: 'facility',
    label: 'Инфраструктура',
    items: [
      { kind: 'rack', type: 'crac',         label: 'CRAC',          icon: Wind,        desc: 'Кондиционер', color: '#a855f7', tooltip: 'Computer Room Air Conditioning — напольный прецизионный кондиционер (1200×1000мм). Без U-слотов. Влияет на расчёт охлаждения зала.' },
      { kind: 'rack', type: 'inrow_cooler', label: 'In-Row Cooler', icon: Thermometer, desc: 'Рядовой',     color: '#818cf8', tooltip: 'Междурядный кондиционер (300×1000мм). Ставится в ряд между стойками. Охлаждает непосредственно соседние стойки.' },
      { kind: 'rack', type: 'pdu_frame',    label: 'Floor PDU',     icon: PlugZap,     desc: 'Шкаф питания', color: '#fb923c', tooltip: 'Напольный блок распределения питания (600×1000мм). Без U-слотов. Распределяет электропитание на ряд стоек.' },
      { kind: 'rack', type: 'ups',          label: 'ИБП (UPS)',     icon: Zap,         desc: 'Батарейный',  color: '#f97316', tooltip: 'Источник бесперебойного питания (800×1000мм). Без U-слотов. Обеспечивает питание при отключении сети.' },
    ] satisfies RackPaletteItem[],
  },
  {
    id: 'architecture',
    label: 'Архитектура',
    items: [
      { kind: 'rack', type: 'pillar',       label: 'Несущая колонна', icon: Square,   desc: 'Препятствие', color: '#a1a1aa', tooltip: 'Несущая колонна здания (600×600мм). Блокирующий элемент — стойки не могут быть размещены на её месте.' },
    ] satisfies RackPaletteItem[],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const ObjectPalette: React.FC = () => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (id: string) =>
    setCollapsed(s => ({ ...s, [id]: !s[id] }));

  const handleRackDragStart = (e: React.DragEvent, type: RackType) => {
    e.dataTransfer.setData('rackType', type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleZoneDragStart = (e: React.DragEvent, type: AisleType) => {
    e.dataTransfer.setData('zoneType', type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div style={{ padding: '6px 0' }}>
      <div style={{
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--text-muted, #6b7280)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        padding: '0 12px 8px',
      }}>
        Объекты
      </div>

      {GROUPS.map(group => {
        const isOpen = !collapsed[group.id];
        return (
          <div key={group.id} style={{ marginBottom: 2 }}>
            {/* Group header */}
            <button
              onClick={() => toggle(group.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '5px 12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-secondary, #9ca3af)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textAlign: 'left',
              }}
            >
              {isOpen
                ? <ChevronDown size={12} style={{ flexShrink: 0 }} />
                : <ChevronRight size={12} style={{ flexShrink: 0 }} />}
              {group.label}
            </button>

            {/* Group items */}
            {isOpen && group.items.map(item => (
              <div
                key={item.kind === 'rack' ? item.type : `zone-${item.type}`}
                draggable
                onDragStart={e =>
                  item.kind === 'rack'
                    ? handleRackDragStart(e, item.type as RackType)
                    : handleZoneDragStart(e, item.type as AisleType)
                }
                title={item.tooltip || `Перетащите: ${item.label}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '6px 12px 6px 20px',
                  cursor: 'grab',
                  borderRadius: 5,
                  margin: '1px 6px',
                  transition: 'background 0.12s',
                  userSelect: 'none',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{
                  width: 26, height: 26,
                  background: item.color + '1a',
                  border: `1px solid ${item.color}44`,
                  borderRadius: 5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <item.icon size={13} color={item.color} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary, #e2e8f0)', fontWeight: 500 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)' }}>
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {/* Hint */}
      <div style={{
        margin: '10px 12px 4px',
        padding: '8px 10px',
        background: 'var(--color-accent-blue-bg)',
        border: '1px dashed var(--color-accent-blue-border)',
        borderRadius: 5,
        fontSize: 10,
        color: 'var(--text-muted, #94a3b8)',
        lineHeight: 1.5,
        display: 'flex',
        gap: 7,
        alignItems: 'flex-start',
      }}>
        <HelpCircle size={12} style={{ color: 'var(--color-accent-blue)', marginTop: 1, flexShrink: 0 }} />
        <span>
          Перетащите объект · <kbd style={{ background: 'var(--bg-input)', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-data)', color: 'var(--text-primary)', fontSize: 9 }}>D</kbd> рисовать трассу ·{' '}
          <kbd style={{ background: 'var(--bg-input)', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-data)', color: 'var(--text-primary)', fontSize: 9 }}>V</kbd> выбор
        </span>
      </div>
    </div>
  );
};
